import React, { memo, useEffect, useState } from 'react';
import cn from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';

import { Icon } from 'components';
import { Button, Form } from 'components/form';
import styles from './SelectWithInputValue.module.scss';

export interface Option {
  id: string;
  value: string;
  label: string;
}

interface Inputs {
  selectField: string;
  numberField: number;
}

export interface SelectWithInputValueProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** options */
  options: Array<Option>;
  /** placholder */
  placholder?: string;
  /** value of select field */
  selectValue?: string;
  /** value of number field */
  numberValue?: number;
  /** on remove handler */
  onRemove?: () => void;
  /** on save handler */
  // @ts-ignore
  onSave: (value: Inputs) => Promise<unknown>;
  /**is remove button loading */
  isRemoveLoading?: boolean;
}

export const SelectWithInputValue: React.FC<SelectWithInputValueProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    options,
    placholder,
    selectValue = '',
    numberValue,
    onRemove,
    onSave,
    isRemoveLoading = false,
  }: SelectWithInputValueProps) => {
    const contentClassNames = cn(styles.SelectWithInputValue, className);

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty },
    } = useForm({
      defaultValues: { selectField: selectValue, numberField: numberValue },
    });

    const [isSaveLoading, setIsSaveLoading] = useState(false);

    useEffect(() => {
      reset({ selectField: selectValue, numberField: numberValue });
    }, [numberValue]);

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
      setIsSaveLoading(true);
      if (data.selectField) {
        await onSave({ ...data, numberField: Number(data.numberField) });
        reset();
        setIsSaveLoading(false);
      } else {
        const body = {
          ...data,
          selectField: selectValue,
          numberField: Number(data.numberField),
        };
        await onSave(body);
        reset(body);
        setIsSaveLoading(false);
      }
    };

    return (
      <Form
        className={contentClassNames}
        style={style}
        data-testid="SelectWithInputValue"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.SelectWithInputValueWrapper}>
          {errors.selectField?.message || errors.numberField?.message ? (
            <p className={styles.SelectWithInputValueError}>
              <span>{errors.selectField?.message}</span>
              <span>{errors.numberField?.message}</span>
            </p>
          ) : null}
          <div className={styles.SelectWithInputValueContainer}>
            <select
              disabled={!!selectValue}
              {...register('selectField', {
                required: 'Required',
              })}
            >
              <option value="">{placholder}</option>
              {options.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Icon
              iconName="chevron-down"
              size="small"
              className={styles.SelectWithInputValueChevron}
            />
            <input
              type="number"
              {...register('numberField', {
                required: 'Required',
                pattern: {
                  value: /^\d*$/,
                  message: 'Only number is allowed',
                },
              })}
            />
          </div>
        </div>
        <div className={styles.SelectWithInputValueAction}>
          <Button
            color="primary"
            size="small"
            type="submit"
            className={styles.SelectWithInputValueActionButton}
            disabled={!isDirty}
            isLoading={isSaveLoading}
          >
            Save
          </Button>
          <Button
            color="danger"
            size="small"
            type="button"
            borderless
            className={cn(styles.SelectWithInputValueActionButton, {
              [styles.SelectWithInputValueActionButtonHidden]: !selectValue,
            })}
            onClick={onRemove}
            isLoading={isRemoveLoading}
          >
            Remove
          </Button>
        </div>
      </Form>
    );
  }
);

SelectWithInputValue.displayName = 'SelectWithInputValue';
